import { menuData, type MenuItem } from '@/config/menu'
import { usePermissions } from '@/composables/usePermissions'
import { usePath } from '@/composables/usePath'
import { useProfileStore } from '@/stores/profile'

export const ACCES_REFUSE_PATH = '/auth/acces-refuse'

export function useHomePath() {
  const { mapPath } = usePath()
  const profileStore = useProfileStore()
  const { hasPermission, hasAnyRole, hasAnyGroup, isSuperAdmin } = usePermissions()

  function isItemAllowed(item: MenuItem): boolean {
    if (item.has_restriction_for_admin && isSuperAdmin.value) return false
    if (isSuperAdmin.value) return true

    const conds: boolean[] = []
    if (item.requirePermissionsAny?.length) {
      conds.push(item.requirePermissionsAny.some(action => hasPermission(action)))
    }
    if (item.requireRolesAny?.length) {
      conds.push(hasAnyRole(item.requireRolesAny))
    }
    if (item.requireGroupsAny?.length) {
      conds.push(hasAnyGroup(item.requireGroupsAny))
    }
    if (item.isVisible) {
      conds.push(item.isVisible({
        hasPermission,
        hasAnyRole,
        hasAnyGroup,
        isSuperAdmin: isSuperAdmin.value,
      }))
    }
    return conds.length === 0 ? true : conds.some(Boolean)
  }

  function hasVisibleMenu(items: MenuItem[] = menuData): boolean {
    return items.some((item) => {
      const childrenOk = item.children?.length ? hasVisibleMenu(item.children) : false
      return (item.to && isItemAllowed(item)) || childrenOk
    })
  }

  function groupNames(): string[] {
    return (profileStore.profile?.groups ?? []).map((group: { name?: string }) =>
      (group.name || '').toString().trim().toLowerCase(),
    )
  }

  function hasRawGroup(...names: string[]): boolean {
    const set = new Set(groupNames())
    return names.some(name => set.has(name.toLowerCase()))
  }

  function isLandingPath(path: string): boolean {
    const normalized = path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path
    return normalized === '' || normalized === '/' || normalized === '/admin'
  }

  function homePath(): string {
    if (!profileStore.profile) return '/'
    if (profileStore.isStudent) return mapPath('/planning/calendar')
    if (!isSuperAdmin.value && !hasVisibleMenu()) return ACCES_REFUSE_PATH
    if (isSuperAdmin.value || profileStore.isAdmin) return mapPath('/dashboard')
    if (profileStore.isComptable || hasAnyGroup(['Comptabilite'])) return mapPath('/comptabilite/factures')
    if (hasRawGroup('maintenance')) return mapPath('/infrastructure')
    if (profileStore.isEnseignant && !hasRawGroup('responsable pedagogique', 'pedagogie')) {
      return mapPath('/planning/enseignant')
    }
    return mapPath('/dashboard')
  }

  /** Après login : navigation complète, sans se faire annuler par le middleware du login. */
  function leaveToHome() {
    const dest = homePath()
    if (import.meta.client) {
      window.location.replace(dest)
      return
    }
    return navigateTo(dest, { replace: true })
  }

  return { homePath, hasVisibleMenu, isLandingPath, leaveToHome, ACCES_REFUSE_PATH }
}
